import { getExpertAvailability, getExpertName, getExpertPhoto } from '../../lib/expertUtils';

const ONLINE_DISPLAY_LIMIT = 100;

export default function OnlineNowBar({ experts, onSelectExpert, loading, gatedCap = 0, onGatedClick, onPhotoError }) {
  const onlineExperts = experts.filter(
    (expert) => getExpertAvailability(expert) === 'online'
  );

  // Photos are public. Anonymous + no-KYC users still get a count cap (so
  // they're nudged to sign up) but the avatars themselves are never blurred.
  const displayLimit = gatedCap > 0 ? Math.min(gatedCap, ONLINE_DISPLAY_LIMIT) : ONLINE_DISPLAY_LIMIT;
  const visibleExperts = onlineExperts.slice(0, displayLimit);
  const displayCount = onlineExperts.length >= ONLINE_DISPLAY_LIMIT ? `${ONLINE_DISPLAY_LIMIT}+` : onlineExperts.length;
  const isGated = gatedCap > 0;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-3">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Online Now
        </p>
        <div className="flex gap-4 overflow-x-auto scrollbar-hide">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 flex flex-col items-center gap-1.5 animate-pulse">
              <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (onlineExperts.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-3">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
          Online Now
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          No one is online right now. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-3">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
        Online Now
        <span className="ml-2 inline-flex min-w-5 h-5 px-1.5 items-center justify-center bg-emerald-500 text-white text-[10px] font-bold rounded-full">
          {displayCount}
        </span>
      </p>
      <div
        className="flex gap-4 overflow-x-auto pb-1"
        style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style jsx>{`
          div::-webkit-scrollbar { display: none; }
        `}</style>
        {visibleExperts.map((expert) => {
          const name = getExpertName(expert);
          const photoURL = getExpertPhoto(expert);
          const firstName = name.split(' ')[0];

          return (
            <button
              key={expert.id || expert.uid}
              onClick={() => (isGated && onGatedClick ? onGatedClick() : onSelectExpert(expert))}
              className="flex-shrink-0 flex flex-col items-center gap-1.5 w-16 group"
              style={{ scrollSnapAlign: 'start' }}
            >
              <div className="relative">
                {/* Green ring */}
                <div className="w-14 h-14 rounded-full p-[2.5px] bg-gradient-to-tr from-emerald-400 to-green-500">
                  {photoURL ? (
                    <img
                      src={photoURL}
                      alt={name}
                      className="w-full h-full rounded-full object-cover border-2 border-white dark:border-gray-900"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        onPhotoError?.(expert, photoURL);
                      }}
                    />
                  ) : null}
                  <div
                    className={`${photoURL ? 'hidden' : 'flex'} w-full h-full rounded-full border-2 border-white dark:border-gray-900 bg-gradient-to-br from-emerald-400 to-blue-500 items-center justify-center`}
                  >
                    <span className="text-white text-sm font-bold">
                      {name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                {/* Green dot */}
                <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse" />
              </div>
              <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-[64px] group-hover:text-emerald-600 transition-colors">
                {firstName}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
